import React from 'react';
import { injectIntl } from 'react-intl';
import { Button, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { sendSegment } from 'utils/segment';
import { ModalContext } from 'contexts/modalProvider.context';

import PreviewImage from 'components/SupportTickets/components/PreviewImage/PreviewImage';
import { notify } from 'components/Notify/Notify';
import DragableUploadListItem from 'components/SupportTickets/components/DragableUploadListItem/DragableUploadListItem';
import BRButton from 'components/BRButton/BRButton';

import uploadImage from 'assets/bosta-icons/Upload-Attachment.svg';

import './UploadAttachment.less';

function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

function getBinary(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

class UploadAttachment extends React.Component {
  state = {
    fileList: [],
    imageValid: false,
    isSendLoading: false
  };
  static contextType = ModalContext;

  uploadRef = React.createRef();

  componentDidUpdate(prevProps) {
    const { toggleUploadDialog = false, startList } = this.props;
    if (startList?.length !== prevProps.startList?.length) {
      this.setState({
        fileList: startList
      });
    }
    if (
      prevProps?.toggleUploadDialog !== toggleUploadDialog &&
      toggleUploadDialog
    ) {
      this.uploadRef.current.upload.uploader.onClick();
    }
  }

  getAttachments = async () => {
    const { fileList } = this.state;
    const attachments = [];
    const attachmentsTypes = [];
    const allFilesData = [];
    await Promise.all(
      fileList.map(async (item) => {
        if (!item.url && !item.preview) {
          item.preview = this.props.encodeBase64
            ? await getBase64(item.originFileObj)
            : await getBinary(item.originFileObj);
        }
        attachments.push(item.preview);
        attachmentsTypes.push(item.type);
        allFilesData.push({
          uid: item.uid,
          lastModified: item.lastModified,
          lastModifiedDate: item.lastModifiedDate,
          name: item.name,
          size: item.size,
          type: item.type,
          webkitRelativePath: item.webkitRelativePath
        });
      })
    );
    return { attachments, attachmentsTypes, allFilesData, fileList };
  };

  getTotalFilesSize = (filesList) =>
    filesList.reduce(
      (accumulator, currentValue) => accumulator + currentValue.size,
      0
    );

  handleChange = async (info) => {
    // if (info.fileList.length < 11) {
    if (
      info?.file?.status === 'removed' &&
      (this.props.deleteImageEventTrackingName ||
        this.props.deleteFileEventTrackingName)
    ) {
      if (info?.file?.isImage || info?.file?.type?.split('/')[0] === 'image') {
        sendSegment(this.props.deleteImageEventTrackingName);
      } else {
        sendSegment(this.props.deleteFileEventTrackingName);
      }
    }

    if (
      this.props.noMaxTotalSize ||
      this.getTotalFilesSize(info.fileList) / 1024 / 1024 < 15
    ) {
      this.setState({
        fileList: info.fileList.filter((file) => !!file.status)
      });
    }
    this.props?.isImagesStillUploading?.(
      info.fileList.find((file) => file?.status === 'uploading')
    );
    this.props.onChange && this.props.onChange(await this.getAttachments());
    // }
  };

  onCloseAction = async ({ file, rotateLeft, rotateRight }) => {
    const { onClosePreview } = this.props;
    if (!rotateLeft && !rotateRight) {
      return;
    }
    try {
      const newUrl = await onClosePreview({
        file,
        rotateLeft,
        rotateRight
      });
      const oldFileList = this.state.fileList;
      const newFileList = oldFileList.map((item) =>
        file.uid === item.uid && file.name === item.name
          ? {
              ...file,
              url: newUrl,
              thumbUrl: newUrl
            }
          : item
      );
      this.setState({
        fileList: newFileList
      });
    } catch (error) {
      notify({ msg: error.message });
    }
  };

  isImage(url) {
    return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
  }

  handlePreview = async (file) => {
    const { openModal } = this.context;
    const { showPreviewActions } = this.props;
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    if (
      file?.isImage ||
      file?.type?.split('/')[0] === 'image' ||
      this.isImage(file?.url)
    )
      openModal(PreviewImage, {
        previewImage: file.url || file.preview,
        showPreviewActions: showPreviewActions || false,
        onCloseAction: (values) => this.onCloseAction({ file, ...values }),
        previewImageTitle:
          file.name || file.url.substring(file.url.lastIndexOf('/') + 1)
      });
    this.props.previewEventTrackingName &&
      sendSegment(this.props.previewEventTrackingName);
  };

  customRequest = ({ onSuccess }) => {
    setTimeout(() => {
      onSuccess();
    }, 0);
  };

  beforeUpload = (file) => {
    const { intl } = this.props;
    const isLt2M = file.size / 1024 / 1024 <= 2;
    if (!isLt2M) {
      notify({
        msg: intl.formatMessage({
          id: 'international_shipping_in.upload_modal.size_error'
        })
      });
    }
    return isLt2M;
  };

  moveRow = (dragIndex, hoverIndex) => {
    const { onDragSuccess } = this.props;
    const newFileList = this.state.fileList;
    newFileList[dragIndex] = [
      newFileList[hoverIndex],
      (newFileList[hoverIndex] = newFileList[dragIndex])
    ][0];
    this.setState({ fileList: newFileList });
    onDragSuccess?.({ dragIndex, hoverIndex });
  };

  handleWithDrag = (originNode, file, currFileList) => {
    const { itemFooterLabel, itemFooterLabelIndex } = this.props;

    if (this.props.withDrag) {
      return (
        <DragableUploadListItem
          originNode={originNode}
          file={file}
          fileList={currFileList}
          moveRow={this.moveRow}
          itemFooterLabel={itemFooterLabel}
          itemFooterLabelIndex={itemFooterLabelIndex}
        />
      );
    } else {
      return originNode;
    }
  };

  render() {
    const { fileList } = this.state;
    const {
      intl,
      maxCount = 1,
      accept = null,
      customSubtitle = '',
      customTitle,
      hidePlusIcon = false,
      sizeLimit = false,
      encodeBase64 = true,
      haveUploadButton = false,
      customButton,
      beforeUpload,
      customUploadContent = false,
      listType = 'picture-card',
      onRemove,
      uploadTitle,
      uploadIcon,
      uploadButton,
      hideUploadButtonAfterMaxCount,
      setUploadRef,
      customRequest,
      progress,
      multiple = true,
      showUploadList = true,
      withTitle = true,
      withSubtitle = true,
      children,
      itemRender
    } = this.props;
    const uploadContent = () => {
      if (customUploadContent) {
        return customUploadContent();
      }
      if (fileList.length === 0) {
        return (
          <div className="br-upload-dialog__empty-view">
            <span className="br-upload-dialog__empty-view__image">
              <img alt="" src={uploadIcon || uploadImage} />
            </span>
            <span className="br-upload-dialog__empty-view__title">
              <span className="br-upload-dialog__empty-view__upload-title-part">
                {uploadTitle ||
                  intl.formatMessage({
                    id: 'support_tickets.upload_images.click_to_upload'
                  })}
              </span>
              {withTitle &&
                (customTitle ||
                  intl.formatMessage({
                    id: 'support_tickets.upload_images.title'
                  }))}
            </span>
            {withSubtitle && (
              <span className="br-upload-dialog__empty-view__sub-title">
                {customSubtitle ||
                  intl.formatMessage({
                    id: 'support_tickets.upload_images.sub_title'
                  })}
              </span>
            )}
            {haveUploadButton &&
              (customButton || (
                <BRButton
                  className="br-upload-dialog__empty-view__upload-button"
                  type="outline"
                  label={intl.formatMessage({
                    id: 'support_tickets.upload_images.upload'
                  })}
                />
              ))}
          </div>
        );
      }
      // } else if (fileList.length < 10 && fileList.length > 0) {
      return (
        uploadButton || (
          <div className="br-upload-dialog__upload-card">
            {!hidePlusIcon && <PlusOutlined />}
            <span>
              {intl.formatMessage({
                id: 'support_tickets.upload_images.upload'
              })}
            </span>
          </div>
        )
      );
      // } else {
      //   return null;
      // }
    };

    return (
      <DndProvider backend={HTML5Backend}>
        <Upload
          ref={setUploadRef || this.uploadRef}
          className={classnames(
            'br-upload-dialog__upload',
            {
              'br-upload-dialog__upload-empty': !fileList.length
            },
            {
              'br-hide-upload-button':
                hideUploadButtonAfterMaxCount && fileList.length === maxCount
            }
          )}
          listType={listType}
          accept={accept || '.jpg, .png, .csv, .xlsx'}
          fileList={fileList}
          multiple={multiple}
          customRequest={customRequest || this.customRequest}
          onPreview={this.handlePreview}
          onChange={this.handleChange}
          onRemove={onRemove}
          maxCount={this.props.maxCount}
          beforeUpload={beforeUpload || this.beforeUpload}
          progress={progress}
          showUploadList={showUploadList}
          itemRender={itemRender || this.handleWithDrag}
        >
          {children || uploadContent()}
        </Upload>
      </DndProvider>
    );
  }
}

export default injectIntl(UploadAttachment);
